// HRUpominkaAddModal — modal pro přidání upomínky.
//
// Workflow:
//   1. Vybereš osobu (select), datum (default dnes), důvod (textarea).
//   2. Pokud bude poradi >= 4, ukáže se pole "Částka pokuty" (Kč).
//   3. Klik "Přidat" → POST /api/hr-upominky-add.
//
// Pre-check: po výběru osoby zobrazí kolik upomínek už má v měsíci a indikuje
// jestli bude tato 4. (pokuta).

import React, { useState } from "react";
import useEscClose from "../lib/useEscClose.js";
import { Plus, AlertTriangle, Loader2, AlertCircle, Coins } from "lucide-react";
import { apiFetch, useApiData } from "../lib/api.js";
import { Modal, HlavickaOkna } from "./ui.jsx";

function poziceLabel(p) {
  return p === "Trener" ? "Trenér" : (p === "Nutricni specialista" ? "Nutriční specialista" : p || "—");
}

export default function HRUpominkaAddModal({ onClose, onSaved, defaultPersonId = null }) {
  useEscClose(onClose); // Esc zavre dialog
  const today = new Date().toISOString().substring(0, 10);
  const [personId, setPersonId] = useState(defaultPersonId || "");
  const [datum, setDatum] = useState(today);
  const [duvod, setDuvod] = useState("");
  const [castka, setCastka] = useState("");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Načti seznam osob
  const { data: peopleData } = useApiData("/api/hr-list-people?filter=vse&status=aktivni");
  const people = peopleData?.people || [];

  // Načti počet upomínek osoby v aktuálním měsíci (k indikaci jestli bude pokuta)
  const mesic = datum.substring(0, 7);
  const { data: existing } = useApiData(personId ? `/api/hr-upominky-list?personId=${personId}&mesic=${mesic}` : null);
  const existingCount = (existing?.entries || []).length;
  const noveporadi = existingCount + 1;
  const budePokuta = noveporadi >= 4;

  const handleSubmit = async () => {
    if (!personId) return setError("Vyber osobu.");
    if (!duvod.trim()) return setError("Vyplň důvod.");
    setSaving(true);
    setError(null);
    try {
      const body = {
        personId,
        datum_upominky: datum,
        duvod: duvod.trim(),
        ...(budePokuta && castka ? { castka_pokuty: parseInt(castka, 10) } : {}),
      };
      const r = await apiFetch("/api/hr-upominky-add", {
        method: "POST",
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      onSaved && onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      plnaHlavicka
      header={<HlavickaOkna ikona={AlertTriangle} nazev="Přidat upomínku" onClose={onClose} />}
      footer={
        <>
          <button onClick={onClose} className="btn btn-secondary">Zrušit</button>
          <button
            type="button"
            disabled={!personId || !duvod.trim() || saving}
            onClick={handleSubmit}
            className="btn btn-primary"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Přidat
          </button>
        </>
      }
    >
      <div className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 inline mr-2" /> {error}
            </div>
          )}

          <div>
            <label className="text-xs text-slate-500 font-medium block mb-1">Osoba *</label>
            <select
              value={personId}
              onChange={(e) => setPersonId(e.target.value)}
              className="field w-full"
            >
              <option value="">Vyber osobu</option>
              {people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.display_name} ({poziceLabel(p.pozice)})
                </option>
              ))}
            </select>
          </div>

          {personId && (
            <div className={`text-xs rounded-xl p-2.5 border ${
              budePokuta ? "bg-rose-50 border-rose-200 text-rose-800"
              : noveporadi === 3 ? "bg-amber-50 border-amber-200 text-amber-800"
              : "bg-slate-50 border-slate-200 text-slate-700"
            }`}>
              V měsíci {mesic} má osoba zatím <b>{existingCount}</b> upomínek. Tato bude <b>{noveporadi}.</b>
              {budePokuta && <span className="block mt-1 font-semibold">Bude to pokuta (čtvrtá a další upomínka).</span>}
              {noveporadi === 3 && <span className="block mt-1">Pozor, příští upomínka bude pokuta.</span>}
            </div>
          )}

          <div>
            <label className="text-xs text-slate-500 font-medium block mb-1">Datum upomínky *</label>
            <input
              type="date"
              value={datum}
              onChange={(e) => setDatum(e.target.value)}
              className="field w-full"
            />
          </div>

          <div>
            <label className="text-xs text-slate-500 font-medium block mb-1">Důvod *</label>
            <textarea
              value={duvod}
              onChange={(e) => setDuvod(e.target.value)}
              rows={3}
              placeholder="Krátký popis, proč osoba dostává upomínku..."
              className="field w-full resize-none"
            />
          </div>

          {budePokuta && (
            <div>
              <label className="text-xs text-rose-700 font-semibold block mb-1 flex items-center gap-1">
                <Coins className="w-3.5 h-3.5" /> Částka pokuty (Kč)
              </label>
              <input
                type="number"
                value={castka}
                onChange={(e) => setCastka(e.target.value)}
                placeholder="např. 500"
                min="0"
                step="100"
                className="field w-full"
              />
              <div className="text-[11px] text-slate-500 mt-1">Můžeš nechat prázdné a doplnit částku později.</div>
            </div>
          )}
        </div>

    </Modal>
  );
}
